import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useRedirectOnNotFound } from '../../lib/useRedirectOnNotFound'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CheckCircle2,
  ChevronRight,
  Eye,
  Flag,
  LayoutDashboard,
  LifeBuoy,
  Loader2,
  Lock,
  Send,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import {
  supportTicket,
  supportTicketDetail,
  supportTicketsResponse,
} from '../../lib/types'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { FilterRow } from '../../components/filters/FilterRow'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { usePageSize } from '../../lib/display-prefs'

const STATUS_LABEL = {
  open: 'Open',
  reopened: 'Reopened',
  in_progress: 'In progress',
  waiting_customer: 'Waiting on customer',
  resolved: 'Resolved',
  closed: 'Closed',
}

const CATEGORY_LABEL = {
  bug: 'Bug',
  feature_request: 'Feature request',
  billing: 'Billing',
  general: 'General',
}

const PRIORITY_LABEL = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
}

// Per-status / per-priority colours rendered in the SAME pill + chip
// style the Spaces board uses (see StatusPill / PriorityChip in
// SpacePage): a colour-dot tinted pill for status, a filled flag for
// priority. Status colours are hex so the `${color}1f` (~12%) background
// tint resolves; priority reuses the shared `--color-priority-*` tokens
// (identical to Spaces' PRIORITY_META).
const STATUS_COLOR = {
  open: '#0496C7',
  // Warm orange — distinct from Open (cyan) AND from waiting_customer's amber
  // so a reopened ticket reads at a glance in the triage list.
  reopened: '#EA580C',
  in_progress: '#02367B',
  waiting_customer: '#F59E0B',
  resolved: '#1C7249',
  closed: '#64748B',
}

const PRIORITY_COLOR = {
  urgent: 'var(--color-priority-urgent)',
  high: 'var(--color-priority-high)',
  normal: 'var(--color-priority-normal)',
  low: 'var(--color-priority-low)',
}

/** Status badge — colour dot + tinted pill, matching the Spaces board. */
function StatusPill({ status }) {
  const color = STATUS_COLOR[status] ?? '#64748B'
  return (
    <span
      className="inline-flex items-center gap-1.5 w-fit px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider"
      style={{ color, backgroundColor: `${color}1f` }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      {STATUS_LABEL[status]}
    </span>
  )
}

/** Priority chip — filled flag + label, matching the Spaces board. */
function PriorityChip({ priority }) {
  const color = PRIORITY_COLOR[priority] ?? 'var(--color-ink-muted)'
  return (
    <span
      className="inline-flex items-center gap-1.5 w-fit text-xs"
      style={{ color }}
    >
      <Flag size={12} fill="currentColor" strokeWidth={0} />
      {PRIORITY_LABEL[priority]}
    </span>
  )
}

/** Route container — `/super-admin/support-tickets` shows the inbox,
 *  `/super-admin/support-tickets/:ticketId` shows one ticket. */
export function SupportTicketsPage() {
  const { ticketId } = useParams()
  if (ticketId) return <TicketDetail ticketId={ticketId} />
  return <TicketList />
}

const SELECT_CLASS =
  'h-9 px-2.5 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer'

function TicketList() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Honour a `?status=` deep-link (e.g. the Dashboard's "Overdue" tile) so the
  // page opens pre-filtered. Only accept a known filter value; anything else
  // falls back to "all" so a stray param can't 400 the list query.
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = searchParams.get('status')
    return s && (STATUS_LABEL[s] || s === 'overdue') ? s : 'all'
  })
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [limit, setLimit] = usePageSize()
  const qc = useQueryClient()
  const [bulkAct, setBulkAct] = useState(null) // { type, ids, clearSelection } | null
  const [bulkRunning, setBulkRunning] = useState(false)

  // Shared server params for the active Search + Status + Priority +
  // Category filters — used by both the paged query and the export fetch.
  function filterParams() {
    const params = {}
    if (statusFilter !== 'all') params.status = statusFilter
    if (priorityFilter !== 'all') params.priority = priorityFilter
    if (categoryFilter !== 'all') params.category = categoryFilter
    if (search.trim()) params.search = search.trim()
    return params
  }

  const ticketsQuery = useQuery({
    queryKey: [
      'support-tickets',
      statusFilter,
      priorityFilter,
      categoryFilter,
      search,
      page,
      limit,
    ],
    queryFn: async () => {
      const res = await api.get('/super-admin/support-tickets', {
        params: { ...filterParams(), page, limit },
      })
      return supportTicketsResponse.parse(res.data)
    },
    // Keep the previous page's rows on screen while the next page
    // loads, so the table doesn't flash empty on page change.
    placeholderData: (prev) => prev,
  })

  const rows = ticketsQuery.data?.data ?? []
  const pageInfo = ticketsQuery.data?.pagination ?? null

  // Export pages through the filtered feed (bounded); DataTable sorts the
  // current page internally via each column's `sortAccessor`.
  async function fetchAllForExport(scope, ids) {
    const all = []
    for (let p = 1; p <= 50; p += 1) {
      const res = await api.get('/super-admin/support-tickets', {
        params: { ...filterParams(), page: p, limit: 500 },
      })
      const parsed = supportTicketsResponse.parse(res.data)
      all.push(...(parsed.data ?? []))
      const info = parsed.pagination
      if (!info || p >= info.totalPages) break
    }
    if (scope === 'selected' && Array.isArray(ids)) {
      const wanted = new Set(ids.map(String))
      return all.filter((t) => wanted.has(String(t.id)))
    }
    return all
  }

  // Bulk status — fan out to the SAME PATCH the ticket drawer uses, so the
  // server enforces allowed transitions per ticket. Tickets already in the
  // target state are skipped (known page rows); unknown/other-page rows are
  // attempted and the server is the guard.
  const statusById = useMemo(
    () => Object.fromEntries(rows.map((t) => [t.id, t.status])),
    [rows],
  )
  const BULK = {
    resolve: {
      verb: 'Mark resolved', done: 'resolved', target: 'resolved',
      applies: (s) => s !== 'resolved' && s !== 'closed',
    },
    close: {
      verb: 'Close', done: 'closed', target: 'closed',
      // Resolved tickets are locked for the support team (the customer owns
      // that closure) — the API 403s any status change on them, so only
      // still-open tickets can be bulk-closed here.
      applies: (s) => s !== 'closed' && s !== 'resolved',
    },
  }
  const bulkActions = [
    {
      label: 'Mark resolved', icon: CheckCircle2,
      onClick: (ids, ctx) =>
        setBulkAct({ type: 'resolve', ids, clearSelection: ctx?.clearSelection }),
    },
    {
      label: 'Close', icon: Lock,
      onClick: (ids, ctx) =>
        setBulkAct({ type: 'close', ids, clearSelection: ctx?.clearSelection }),
    },
  ]
  async function runBulkStatus() {
    const cfg = BULK[bulkAct.type]
    const ids = bulkAct.ids.filter(
      (id) => statusById[id] == null || cfg.applies(statusById[id]),
    )
    if (ids.length === 0) {
      toast.message('Nothing to do — the action doesn’t apply to the selected tickets.')
      setBulkAct(null)
      return
    }
    setBulkRunning(true)
    let ok = 0
    const fails = []
    for (const id of ids) {
      try {
        await api.patch(`/super-admin/support-tickets/${id}`, { status: cfg.target })
        ok += 1
      } catch (e) {
        fails.push(extractApiError(e).message)
      }
    }
    setBulkRunning(false)
    const skipped = bulkAct.ids.length - ids.length
    if (ok)
      toast.success(
        `${ok} ${ok === 1 ? 'ticket' : 'tickets'} ${cfg.done}.` +
          (skipped ? ` ${skipped} skipped.` : ''),
      )
    if (fails.length)
      toast.error(
        `${fails.length} failed: ${fails[0]}` +
          (fails.length > 1 ? ` (+${fails.length - 1} more)` : ''),
      )
    await qc.invalidateQueries({ queryKey: ['support-tickets'] })
    bulkAct.clearSelection?.()
    setBulkAct(null)
  }

  const columns = useMemo(
    () => [
      {
        key: 'ticketNumber',
        header: 'Ticket',
        minWidth: 110,
        sortAccessor: (t) => (t.ticketNumber ?? '').toLowerCase(),
        exportValue: (t) => t.ticketNumber ?? '',
        render: (t) => (
          <span
            className={
              'w-fit text-[11px] font-semibold tracking-wide rounded px-1.5 py-0.5 ' +
              (t.ticketNumber
                ? 'text-[color:var(--color-ink-muted)] bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)]'
                : 'text-[color:var(--color-ink-subtle)]')
            }
          >
            {t.ticketNumber ?? '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 130,
        sortAccessor: (t) => t.status ?? '',
        exportValue: (t) => STATUS_LABEL[t.status] ?? t.status ?? '',
        render: (t) => <StatusPill status={t.status} />,
      },
      {
        key: 'subject',
        header: 'Subject',
        grow: true,
        pinnable: true,
        minWidth: 240,
        sortAccessor: (t) => (t.subject ?? '').toLowerCase(),
        exportValue: (t) => t.subject ?? '',
        render: (t) => (
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[color:var(--color-ink-strong)] truncate">
              {t.subject}
            </p>
            <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
              {t.description}
            </p>
          </div>
        ),
      },
      {
        key: 'company',
        header: 'Company',
        minWidth: 140,
        sortAccessor: (t) => (t.tenantName ?? '').toLowerCase(),
        exportValue: (t) => t.tenantName ?? '',
        cellClassName: 'text-xs text-[color:var(--color-ink-muted)]',
        render: (t) => (
          <span className="truncate block">{t.tenantName ?? '—'}</span>
        ),
      },
      {
        key: 'category',
        header: 'Category',
        minWidth: 110,
        sortAccessor: (t) => (t.category ?? '').toLowerCase(),
        exportValue: (t) => CATEGORY_LABEL[t.category] ?? t.category ?? '',
        cellClassName: 'text-xs text-[color:var(--color-ink-muted)]',
        render: (t) => CATEGORY_LABEL[t.category] ?? t.category,
      },
      {
        key: 'priority',
        header: 'Priority',
        minWidth: 100,
        sortAccessor: (t) => t.priority ?? '',
        exportValue: (t) => PRIORITY_LABEL[t.priority] ?? t.priority ?? '',
        render: (t) => <PriorityChip priority={t.priority} />,
      },
      {
        key: 'createdAt',
        header: 'Created',
        minWidth: 140,
        sortAccessor: (t) => (t.createdAt ? new Date(t.createdAt) : null),
        exportValue: (t) => (t.createdAt ? formatDateTime(t.createdAt) : ''),
        cellClassName:
          'text-xs text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (t) => formatDateTime(t.createdAt),
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        minWidth: 140,
        sortAccessor: (t) => (t.updatedAt ? new Date(t.updatedAt) : null),
        exportValue: (t) => (t.updatedAt ? formatDateTime(t.updatedAt) : ''),
        cellClassName:
          'text-xs text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (t) => formatDateTime(t.updatedAt),
      },
      {
        key: 'actions',
        // Singular: this column renders exactly ONE row action. Restore the
        // plural if a second is ever added — the eleven columns that already
        // carry two or more use "Actions".
        header: 'Action',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (t) => (
          <RowActionsMenu
            label={`Actions for ticket ${t.ticketNumber ?? ''}`}
            items={[
              {
                key: 'view',
                label: 'View ticket',
                icon: Eye,
                onClick: () => navigate(`/super-admin/support-tickets/${t.id}`),
              },
            ]}
          />
        ),
      },
    ],
    [navigate],
  )

  return (
    <div>
      {/* Breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        className="mb-4 flex items-center gap-2 text-sm text-[color:var(--color-ink-muted)]"
      >
        <Link
          to="/super-admin/dashboard"
          className="flex items-center gap-1 hover:text-[color:var(--color-ink-strong)] cursor-pointer"
        >
          <LayoutDashboard size={14} />
          <span>Dashboard</span>
        </Link>
        <ChevronRight size={14} aria-hidden="true" />
        <span className="text-[color:var(--color-ink-strong)] font-medium">
          Support Tickets
        </span>
      </nav>

      {/* Title — tickets are filed by Company Admins from their own
       *  Support page, so there is no "New ticket" action here. */}
      <div className="mb-5">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)]">
          Support Tickets
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Reply to help requests from companies.
        </p>
      </div>

      {/* Filters — shared FilterRow primitive. Search + three selects
       *  sit on one row so the inbox controls collapse to a single
       *  rhythm. */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Search by Subject or Ticket Number"
        />
        <FilterRow.Select
          ariaLabel="Status"
          placeholder="All Statuses"
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Statuses' },
            ...Object.entries(STATUS_LABEL).map(([v, label]) => ({
              value: v,
              label,
            })),
            // Derived filter (not a stored status): open tickets older than 7
            // days, matching the Dashboard's "Overdue" support-ticket tile.
            { value: 'overdue', label: 'Overdue' },
          ]}
        />
        <FilterRow.Select
          ariaLabel="Priority"
          placeholder="All Priorities"
          value={priorityFilter}
          onChange={(v) => {
            setPriorityFilter(v)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Priorities' },
            ...Object.entries(PRIORITY_LABEL).map(([v, label]) => ({
              value: v,
              label,
            })),
          ]}
        />
        <FilterRow.Select
          ariaLabel="Category"
          placeholder="All Categories"
          value={categoryFilter}
          onChange={(v) => {
            setCategoryFilter(v)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Categories' },
            ...Object.entries(CATEGORY_LABEL).map(([v, label]) => ({
              value: v,
              label,
            })),
          ]}
        />
        <FilterRow.Spacer />
        {/* No manual refresh button — removed for cross-page
         *  consistency (QA-flagged: this was the ONLY page in the app
         *  with one). The list stays live without it: every ticket
         *  mutation emits `data:changed` to the platform room and
         *  RealtimeInvalidator maps the support-ticket resource to
         *  this page's ['support-tickets'] query key, plus TanStack
         *  Query's refetch-on-focus covers the reconnect gap. */}
        <FilterRow.Clear
          show={
            search.trim() !== '' ||
            statusFilter !== 'all' ||
            priorityFilter !== 'all' ||
            categoryFilter !== 'all'
          }
          onClick={() => {
            setSearch('')
            setStatusFilter('all')
            setPriorityFilter('all')
            setCategoryFilter('all')
            setPage(1)
          }}
        />
      </FilterRow>

      {/* Server-paginated inbox on the shared DataTable. Search + Status +
       *  Priority + Category filters above stay page-owned (server params);
       *  the row menu opens the ticket detail route. */}
      <DataTable
        tableId="platform-support-tickets"
        columns={columns}
        rows={rows}
        getRowId={(t) => t.id}
        loading={ticketsQuery.isLoading}
        fetching={ticketsQuery.isFetching}
        error={
          ticketsQuery.isError
            ? extractApiError(ticketsQuery.error).message
            : null
        }
        onRetry={() => ticketsQuery.refetch()}
        searchable={false}
        defaultSort={[{ key: 'updatedAt', dir: 'desc' }]}
        page={pageInfo?.page ?? page}
        totalPages={pageInfo?.totalPages ?? 1}
        total={pageInfo?.total ?? 0}
        limit={pageInfo?.limit ?? limit}
        onPageChange={setPage}
        onLimitChange={(n) => {
          setLimit(n)
          setPage(1)
        }}
        exportBaseName="support-tickets"
        exportTitle="Support Tickets"
        getExportRows={fetchAllForExport}
        selectable
        bulkActions={bulkActions}
        onSelectAllRecords={async () =>
          (await fetchAllForExport()).map((t) => t.id)
        }
        emptyState={{
          icon: LifeBuoy,
          title: 'No records found',
        }}
      />

      {bulkAct
        ? (() => {
            const cfg = BULK[bulkAct.type]
            const known = bulkAct.ids.filter((id) => statusById[id] != null)
            const unknown = bulkAct.ids.length - known.length
            const applicable =
              known.filter((id) => cfg.applies(statusById[id])).length + unknown
            const skipped = bulkAct.ids.length - applicable
            const label = `${applicable} ${applicable === 1 ? 'ticket' : 'tickets'}`
            return (
              <ConfirmDialog
                open
                title={`${cfg.verb} ${label}`}
                message={
                  (applicable > 0
                    ? `This will ${cfg.verb.toLowerCase()} ${label}.`
                    : 'None of the selected tickets can take this action.') +
                  (skipped > 0
                    ? ` ${skipped} will be skipped (already in that state, or resolved and locked).`
                    : '')
                }
                confirmLabel={cfg.verb}
                variant="primary"
                confirmDisabled={applicable === 0}
                confirming={bulkRunning}
                onCancel={() => {
                  if (!bulkRunning) setBulkAct(null)
                }}
                onConfirm={runBulkStatus}
              />
            )
          })()
        : null}
    </div>
  )
}

function InfoRow({ label, children }) {
  return (
    <tr className="border-b border-[color:var(--color-rule)] last:border-b-0">
      <td className="py-2 pr-4 align-top whitespace-nowrap text-xs text-[color:var(--color-ink-muted)]">
        {label}
      </td>
      <td className="py-2 align-top text-sm text-[color:var(--color-ink-strong)]">
        {children}
      </td>
    </tr>
  )
}

function TicketDetail({ ticketId }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [reply, setReply] = useState('')
  const [internalNote, setInternalNote] = useState(false)
  // `null` = "no pending edit, mirror the ticket". A non-null value is
  // the operator's unsaved Admin Actions choice. Resetting to null
  // after a successful save re-syncs the selects to fresh ticket data
  // without needing an effect.
  const [statusOverride, setStatusOverride] = useState(null)
  const [priorityOverride, setPriorityOverride] = useState(null)

  const detailQuery = useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: async () => {
      const res = await api.get(`/super-admin/support-tickets/${ticketId}`)
      return supportTicketDetail.parse(res.data)
    },
  })

  const ticket = detailQuery.data
  // Don't strand the viewer on a dead "Ticket not found." page when the ticket
  // is deleted (or the URL is stale) — return to the list with a notice.
  useRedirectOnNotFound(detailQuery, () => navigate('/super-admin/support-tickets'), 'This ticket is no longer available.')

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['support-ticket', ticketId] })
    void qc.invalidateQueries({ queryKey: ['support-tickets'] })
  }

  const updateMutation = useMutation({
    mutationFn: async (dto) => {
      const res = await api.patch(
        `/super-admin/support-tickets/${ticketId}`,
        dto,
      )
      return supportTicket.parse(res.data)
    },
    onSuccess: () => {
      toast.success('Ticket updated successfully.')
      setStatusOverride(null)
      setPriorityOverride(null)
      invalidate()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const replyMutation = useMutation({
    // The draft travels as mutation VARIABLES and the composer clears
    // in onMutate — the instant Send is clicked. The thread repaints
    // via realtime invalidation as soon as the server fans out, often
    // BEFORE the POST resolves, so clearing in onSuccess left the sent
    // text sitting in the input for the whole round-trip and it read
    // as "not sent" (QA-reported). A failed send restores the draft
    // (and the internal-note flag) so nothing typed is lost.
    mutationFn: async ({ body, isInternalNote }) => {
      await api.post(`/super-admin/support-tickets/${ticketId}/messages`, {
        body,
        isInternalNote,
      })
    },
    onMutate: () => {
      setReply('')
      setInternalNote(false)
    },
    onSuccess: () => {
      invalidate()
      toast.success('Reply sent.')
    },
    onError: (err, vars) => {
      setReply(vars.body)
      setInternalNote(vars.isInternalNote)
      toast.error(extractApiError(err).message)
    },
  })

  if (detailQuery.isLoading) {
    return (
      <div className="grid place-items-center py-24">
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading ticket…
        </p>
      </div>
    )
  }

  if (!ticket) {
    return (
      <div>
        <button
          type="button"
          onClick={() => navigate('/super-admin/support-tickets')}
          className="text-sm text-[color:var(--color-accent-strong)] hover:underline cursor-pointer mb-4"
        >
          ← Back to tickets
        </button>
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {detailQuery.isError
            ? extractApiError(detailQuery.error).message
            : 'Ticket not found.'}
        </p>
      </div>
    )
  }

  const t = ticket
  const label = t.ticketNumber ?? 'Ticket'
  const statusDraft = statusOverride ?? t.status
  const priorityDraft = priorityOverride ?? t.priority
  const dirty = statusDraft !== t.status || priorityDraft !== t.priority
  // Once resolved/closed the ticket is locked for the platform — the
  // customer must reopen it before status can change or we can reply.
  // The backend enforces this too; this just disables the controls so
  // the operator never hits a Forbidden mid-action.
  const isLocked = t.status === 'resolved' || t.status === 'closed'

  return (
    <div>
      {/* Breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        className="mb-4 flex items-center gap-2 text-sm text-[color:var(--color-ink-muted)]"
      >
        <Link
          to="/super-admin/dashboard"
          className="flex items-center gap-1 hover:text-[color:var(--color-ink-strong)] cursor-pointer"
        >
          <LayoutDashboard size={14} />
          <span>Dashboard</span>
        </Link>
        <ChevronRight size={14} aria-hidden="true" />
        <Link
          to="/super-admin/support-tickets"
          className="hover:text-[color:var(--color-ink-strong)] cursor-pointer"
        >
          Support Tickets
        </Link>
        <ChevronRight size={14} aria-hidden="true" />
        <span className="text-[color:var(--color-ink-strong)] font-medium">
          {label}
        </span>
      </nav>

      <div className="mb-5">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] break-words [overflow-wrap:anywhere]">
          {t.subject}
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Ticket {label}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
        {/* Left column */}
        <div className="space-y-5">
          {/* Description */}
          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
            <h2 className="text-[15px] font-semibold text-[color:var(--color-ink-strong)] mb-2">
              Description
            </h2>
            {/* break-words + [overflow-wrap:anywhere] — unbroken tokens
             *  overflowed the card otherwise (QA-reported on the tenant
             *  page; same data renders here). The combo matches the
             *  ConfirmDialog canonical guard. */}
            <p className="text-sm text-[color:var(--color-ink)] whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
              {t.description}
            </p>
            {t.attachments && t.attachments.length > 0 ? (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1.5">
                  Attachments
                </p>
                <div className="flex flex-wrap gap-2">
                  {t.attachments.map((a) => (
                    <a
                      key={a.id}
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={a.filename ?? 'Open attachment'}
                      className="block rounded-md border border-[color:var(--color-rule)] overflow-hidden hover:border-[color:var(--color-accent)] transition-colors"
                    >
                      <img
                        src={a.url}
                        alt={a.filename ?? 'attachment'}
                        className="h-20 w-20 object-cover"
                      />
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* Conversation */}
          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
            <h2 className="text-[15px] font-semibold text-[color:var(--color-ink-strong)] mb-3">
              Conversation ({t.messages.length})
            </h2>

            {t.messages.length === 0 ? (
              <p className="text-sm text-[color:var(--color-ink-muted)] italic pb-3 border-b border-[color:var(--color-rule)]">
                No replies yet.
              </p>
            ) : (
              <div className="space-y-3 pb-3 border-b border-[color:var(--color-rule)]">
                {t.messages.map((m) => (
                  <div
                    key={m.id}
                    className={
                      'rounded-lg border p-3 ' +
                      (m.isInternalNote
                        ? 'border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)]'
                        : 'border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]')
                    }
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
                        {m.authorName ?? 'System'}
                        {m.isInternalNote ? (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-warning)]">
                            <Lock size={9} />
                            Internal note
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-[color:var(--color-ink-muted)]">
                        {formatDateTime(m.createdAt)}
                      </p>
                    </div>
                    <p className="text-sm text-[color:var(--color-ink)] whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                      {m.body}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Reply box — locked once the ticket is resolved/closed.
             *  The customer owns that closure; they must reopen the
             *  ticket before the platform can reply again. */}
            {isLocked ? (
              <p className="mt-3 text-sm text-[color:var(--color-ink-muted)] italic text-center py-3 border-t border-[color:var(--color-rule)]">
                This ticket is {STATUS_LABEL[t.status].toLowerCase()}. The
                customer must reopen it before you can reply or change its
                status.
              </p>
            ) : (
              <div className="mt-3">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  placeholder={
                    internalNote
                      ? 'Internal note (only the support team sees this)…'
                      : 'Reply to the customer…'
                  }
                  className="w-full px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] outline-none focus:border-[color:var(--color-accent)] resize-y text-sm"
                />
                {reply.trim().length > 0 && reply.trim().length < 10 && (
                  <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                    Minimum 10 characters.
                  </p>
                )}
                <div className="mt-2 flex items-center justify-between gap-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[color:var(--color-ink)]">
                    <input
                      type="checkbox"
                      checked={internalNote}
                      onChange={(e) => setInternalNote(e.target.checked)}
                    />
                    Internal note (private to support team)
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      replyMutation.mutate({
                        body: reply.trim(),
                        isInternalNote: internalNote,
                      })
                    }
                    disabled={
                      replyMutation.isPending || reply.trim().length < 10
                    }
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                  >
                    <Send size={13} />
                    {replyMutation.isPending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar */}
        <div className="space-y-5">
          {/* Ticket Information */}
          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
            <h2 className="text-[15px] font-semibold text-[color:var(--color-ink-strong)] mb-2">
              Ticket Information
            </h2>
            <table className="w-full">
              <tbody>
                <InfoRow label="Status">
              <StatusPill status={t.status} />
            </InfoRow>
            <InfoRow label="Priority">
              <PriorityChip priority={t.priority} />
            </InfoRow>
            <InfoRow label="Category">
              {CATEGORY_LABEL[t.category] ?? t.category}
            </InfoRow>
            <InfoRow label="Created By">{t.userName ?? '—'}</InfoRow>
            <InfoRow label="Company">{t.tenantName ?? '—'}</InfoRow>
            <InfoRow label="Created">{formatDateTime(t.createdAt)}</InfoRow>
            <InfoRow label="Updated">{formatDateTime(t.updatedAt)}</InfoRow>
              </tbody>
            </table>
          </div>

          {/* Admin Actions */}
          <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
            <h2 className="text-[15px] font-semibold text-[color:var(--color-ink-strong)] mb-3">
              Admin Actions
            </h2>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
              Change Status
            </label>
            <select
              value={statusDraft}
              onChange={(e) => setStatusOverride(e.target.value)}
              disabled={isLocked}
              className={
                SELECT_CLASS +
                ' w-full' +
                (isLocked ? ' mb-1 opacity-60 cursor-not-allowed' : ' mb-3')
              }
            >
              {Object.entries(STATUS_LABEL)
                /* Support team can move a ticket up to Resolved; only the
                 * customer closes (confirms) or reopens a ticket. "Closed"
                 * and "Reopened" stay selectable ONLY when the ticket already
                 * is in that state, so it displays correctly here without
                 * letting a Master Admin newly set a customer-owned status. */
                .filter(
                  ([v]) =>
                    (v !== 'closed' || t.status === 'closed') &&
                    (v !== 'reopened' || t.status === 'reopened'),
                )
                .map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
            </select>
            {isLocked ? (
              <p className="mb-3 text-xs text-[color:var(--color-ink-muted)]">
                Locked while {STATUS_LABEL[t.status].toLowerCase()} — the
                customer must reopen this ticket to change its status.
              </p>
            ) : null}
            <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
              Change Priority
            </label>
            <select
              value={priorityDraft}
              onChange={(e) => setPriorityOverride(e.target.value)}
              disabled={isLocked}
              className={
                SELECT_CLASS +
                ' w-full mb-4' +
                (isLocked ? ' opacity-60 cursor-not-allowed' : '')
              }
            >
              {Object.entries(PRIORITY_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() =>
                updateMutation.mutate({
                  status: statusDraft,
                  priority: priorityDraft,
                })
              }
              disabled={updateMutation.isPending || !dirty || isLocked}
              className="w-full px-3 py-2.5 rounded-md text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? 'Updating…' : 'Update'}
            </button>
          </div>

          <button
            type="button"
            onClick={() => navigate('/super-admin/support-tickets')}
            className="w-full px-3 py-2.5 rounded-md text-sm font-semibold border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            ← Back to Tickets
          </button>
        </div>
      </div>
    </div>
  )
}
